/**
 * Сравнение КП со сметой (СМ-18, БМ-05): заявка проекта → КП → позиции заявки против позиций смет проекта
 * (сопоставление по коду и словам), цена КП без НДС против сметной цены единицы, итоги по материалам,
 * работам и оборудованию.
 */
import type { OfferCompare, ProjectOffers } from '@epc/contracts';
import { queryOptions, useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import clsx from 'clsx';
import { useState } from 'react';
import { ApiError, api } from '../../lib/api';
import { tengeShort } from '../../lib/format';
import { Badge, EmptyState, ErrorBox, Spinner } from '../../ui/feedback';
import { Field, Select } from '../../ui/form';
import { ScrollX } from '../../ui/scroll';
import { qty, sum } from './lib';

export const projectOffersQuery = (projectId: string) =>
  queryOptions({
    queryKey: ['estimates', 'offers', projectId],
    queryFn: () => api<ProjectOffers>('/estimates/offers', { query: { projectId } }),
  });

export const offerCompareQuery = (p: {
  projectId: string;
  requestId: string;
  offerId: string;
  estimateId?: string;
}) =>
  queryOptions({
    queryKey: ['estimates', 'offer-compare', p],
    queryFn: () => api<OfferCompare>('/estimates/offer-compare', { query: p }),
    enabled: Boolean(p.requestId && p.offerId),
  });

/** Выпадающие списки на телефоне — не меньше 44 px */
const CONTROL = 'min-h-[44px] md:min-h-10';

const GROUPS: Array<[keyof OfferCompare['totals']['offerNet'], string]> = [
  ['materials', 'Материалы'],
  ['works', 'Работы'],
  ['equipment', 'Оборудование'],
  ['other', 'Прочие и несопоставленные'],
  ['total', 'Итого'],
];

/** Выбор заявки и КП проекта; onPick — для варианта «субподряд по КП» */
export function OfferPicker({
  projectId,
  value,
  onChange,
}: {
  projectId: string;
  value: { requestId: string; offerId: string };
  onChange: (v: { requestId: string; offerId: string; total: number | null; supplierName: string }) => void;
}) {
  const q = useQuery(projectOffersQuery(projectId));
  if (q.isPending) return <Spinner />;
  if (q.isError) {
    // Без права на закупки проекта список КП недоступен — сумму можно ввести вручную
    if (q.error instanceof ApiError && q.error.status === 403)
      return (
        <p className="text-[13px] text-ink-2">Заявки и КП видны только с правом на «Закупки» проекта.</p>
      );
    return <ErrorBox error={q.error} />;
  }
  if (!q.data.length)
    return (
      <p className="text-[13px] text-ink-2">В проекте пока нет заявок с КП — они появляются в «Закупках».</p>
    );
  // Вариант, сохранённый с КП, открывается с уже выбранной заявкой
  const req =
    q.data.find((r) => r.requestId === value.requestId) ??
    (value.offerId ? (q.data.find((r) => r.offers.some((o) => o.id === value.offerId)) ?? null) : null);
  return (
    <div className="grid gap-3 sm:grid-cols-2">
      <Field label="Заявка на закуп">
        <Select
          className={CONTROL}
          value={req?.requestId ?? ''}
          onChange={(e) => {
            const r = q.data.find((x) => x.requestId === e.target.value);
            const o = r?.offers[0];
            onChange({
              requestId: e.target.value,
              offerId: o?.id ?? '',
              total: o?.total ?? null,
              supplierName: o?.supplierName ?? '',
            });
          }}
        >
          <option value="">— выберите —</option>
          {q.data.map((r) => (
            <option key={r.requestId} value={r.requestId}>
              {r.number} — {r.title} ({r.offers.length} КП)
            </option>
          ))}
        </Select>
      </Field>
      <Field label="Коммерческое предложение">
        <Select
          className={CONTROL}
          value={value.offerId}
          disabled={!req}
          onChange={(e) => {
            const o = req?.offers.find((x) => x.id === e.target.value);
            onChange({
              requestId: req?.requestId ?? value.requestId,
              offerId: e.target.value,
              total: o?.total ?? null,
              supplierName: o?.supplierName ?? '',
            });
          }}
        >
          <option value="">— выберите —</option>
          {(req?.offers ?? []).map((o) => (
            <option key={o.id} value={o.id}>
              {o.supplierName} — {tengeShort(o.total)}
              {o.isSelected ? ' · выбрано' : ''}
              {o.missing ? ` · нет цен: ${o.missing}` : ''}
            </option>
          ))}
        </Select>
      </Field>
    </div>
  );
}

export function OfferCompareTab({ projectId, estimateId }: { projectId: string; estimateId?: string }) {
  const [pick, setPick] = useState({ requestId: '', offerId: '' });
  const [onlyThis, setOnlyThis] = useState(Boolean(estimateId));
  const cmp = useQuery(
    offerCompareQuery({ projectId, ...pick, estimateId: onlyThis ? estimateId : undefined }),
  );
  return (
    <div className="flex flex-col gap-4">
      <OfferPicker
        projectId={projectId}
        value={pick}
        onChange={(v) => setPick({ requestId: v.requestId, offerId: v.offerId })}
      />
      {estimateId ? (
        <label className="flex min-h-[44px] items-center gap-2 text-[13px] text-ink-2 md:min-h-0">
          <input type="checkbox" checked={onlyThis} onChange={(e) => setOnlyThis(e.target.checked)} />
          сопоставлять только с этой сметой (иначе — со всеми текущими сметами проекта)
        </label>
      ) : null}
      {!pick.offerId ? null : cmp.isPending ? (
        <Spinner />
      ) : cmp.isError ? (
        <ErrorBox error={cmp.error} />
      ) : (
        <CompareTable c={cmp.data} />
      )}
    </div>
  );
}

function CompareTable({ c }: { c: OfferCompare }) {
  const money = c.offer.total !== null;
  return (
    <div className="flex flex-col gap-4">
      <div className="grid grid-cols-2 gap-px bg-line text-[13px] sm:grid-cols-5">
        {GROUPS.map(([k, title]) => {
          const o = c.totals.offerNet[k];
          const e = c.totals.estimate[k];
          // У «Прочих» и «Итого» в сумму КП входят несопоставленные строки, у сметы — нет: процент там не честный
          const comparable = c.totals.unmatched === 0 || (k !== 'other' && k !== 'total');
          const d =
            comparable && o !== null && e !== null && e ? Math.round(((o - e) / e) * 1000) / 10 : null;
          return (
            <div key={k} className="bg-surface px-3 py-2">
              <span className="block text-[12px] text-ink-2">{title}</span>
              <span className="num block">КП без НДС {money ? tengeShort(o) : '—'}</span>
              <span className="num block text-ink-2">смета {money ? tengeShort(e) : '—'}</span>
              {d !== null ? (
                <span className={clsx('num block font-medium', d > 0 ? 'text-danger' : 'text-success')}>
                  {d > 0 ? '+' : ''}
                  {d}%
                </span>
              ) : null}
            </div>
          );
        })}
      </div>
      <p className="text-[12px] text-ink-2">
        КП «{c.offer.supplierName}» по заявке {c.request.number} «{c.request.title}»
        {c.offer.deliveryIncluded ? ', доставка включена' : ''}. Цены КП с НДС делятся на 1,16; сметная цена
        единицы — ПЗ позиции (у оборудования — ОБ). Не сопоставлено позиций: {c.totals.unmatched}.
      </p>
      {c.lines.length ? (
        <>
          <ul className="divide-y divide-line md:hidden">
            {c.lines.map((l) => (
              <li key={l.lineId} className="flex flex-col gap-1 py-3 text-[13px]">
                <span className="font-medium [overflow-wrap:anywhere]">{l.name}</span>
                <span className="text-ink-2">
                  {qty(l.quantity)} {l.unit} · КП {money ? sum(l.offerPrice) : '—'} с НДС /{' '}
                  {money ? sum(l.offerPriceNet) : '—'} без
                </span>
                {l.estimateItem ? (
                  <span className="text-ink-2 [overflow-wrap:anywhere]">
                    Смета {l.estimateItem.estimateCode} поз. {l.estimateItem.number}: {l.estimateItem.name} —{' '}
                    {money ? sum(l.estimateItem.unitPrice) : '—'} <Badge>{l.estimateItem.confidence}%</Badge>
                  </span>
                ) : (
                  <Badge tone="warning">в смете не найдено</Badge>
                )}
                {l.diffPct !== null ? (
                  <span className={clsx('num font-medium', l.diffPct > 0 ? 'text-danger' : 'text-success')}>
                    {l.diffPct > 0 ? '+' : ''}
                    {l.diffPct}%
                  </span>
                ) : null}
                {l.history && money ? (
                  <span className="text-ink-2">
                    Прошлые закупки ({l.history.count}): мин. {sum(l.history.min)}, посл.{' '}
                    {sum(l.history.last)}
                    {l.history.lastSupplier ? ` — ${l.history.lastSupplier}` : ''}
                  </span>
                ) : null}
              </li>
            ))}
          </ul>
          <div className="hidden md:block">
            <ScrollX stickyFirst={false}>
              <table className="w-full min-w-[900px] text-[13px]">
                <thead>
                  <tr className="bg-surface-2 text-left text-[12px] text-ink-2">
                    <th className="py-2 pr-2 pl-4 font-medium">Позиция заявки</th>
                    <th className="py-2 pr-2 text-right font-medium">Кол-во</th>
                    <th className="py-2 pr-2 text-right font-medium">КП с НДС</th>
                    <th className="py-2 pr-2 text-right font-medium">КП без НДС</th>
                    <th className="py-2 pr-2 font-medium">Позиция сметы</th>
                    <th className="py-2 pr-2 text-right font-medium">Сметная цена</th>
                    <th className="py-2 pr-2 text-right font-medium">Отклонение</th>
                    <th className="py-2 pr-4 text-right font-medium">Прошлые закупки, без НДС</th>
                  </tr>
                </thead>
                <tbody>
                  {c.lines.map((l) => (
                    <tr key={l.lineId} className="border-t border-line align-top">
                      <td className="py-2 pr-2 pl-4 [overflow-wrap:anywhere]">
                        {l.name} <span className="text-ink-2">· {l.unit}</span>
                      </td>
                      <td className="num py-2 pr-2 text-right">{qty(l.quantity)}</td>
                      <td className="num py-2 pr-2 text-right">{money ? sum(l.offerPrice) : '—'}</td>
                      <td className="num py-2 pr-2 text-right">{money ? sum(l.offerPriceNet) : '—'}</td>
                      <td className="py-2 pr-2 [overflow-wrap:anywhere]">
                        {l.estimateItem ? (
                          <>
                            <Link
                              to="/estimates/$id"
                              params={{ id: l.estimateItem.estimateId }}
                              className="text-accent-text underline-offset-2 hover:underline"
                            >
                              {l.estimateItem.estimateCode} поз. {l.estimateItem.number}
                            </Link>{' '}
                            {l.estimateItem.name} <Badge>{l.estimateItem.confidence}%</Badge>
                          </>
                        ) : (
                          <Badge tone="warning">не найдено</Badge>
                        )}
                      </td>
                      <td className="num py-2 pr-2 text-right">
                        {l.estimateItem && money ? sum(l.estimateItem.unitPrice) : '—'}
                      </td>
                      <td
                        className={clsx(
                          'num py-2 pr-4 text-right font-medium',
                          l.diffPct !== null && (l.diffPct > 0 ? 'text-danger' : 'text-success'),
                        )}
                      >
                        {l.diffPct !== null ? `${l.diffPct > 0 ? '+' : ''}${l.diffPct}%` : '—'}
                      </td>
                      <td className="num py-2 pr-4 text-right text-ink-2">
                        {l.history && money
                          ? `${sum(l.history.min)} … ${sum(l.history.last)} (${l.history.count})${l.history.lastSupplier ? ` — ${l.history.lastSupplier}` : ''}`
                          : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </ScrollX>
          </div>
        </>
      ) : (
        <EmptyState title="В заявке нет позиций" />
      )}
    </div>
  );
}
